import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionCount,
  SectionContent,
  ZoneType,
  ExpandableContent,
  ExpandableDetails,
  PropertyDataRow as DataRow,
  DataLabel,
  DataValue,
} from '../../../molecules/PropertyPanelStyles'
import { ExpandArrow } from '../../../atoms/ExpandArrow'
import { Icon } from '../../../atoms/Icon'
import type { LandZoningReference, ZoningType } from '@popety-io/types'

/**
 * Colors per zoning type, mirroring ZONING_TYPE_COLORS in the
 * land analysis ZoningSection (libs/ui/src/components/organisms/ZoningSection).
 */
const ZONING_TYPE_COLORS: Record<ZoningType, string> = {
  cantonal: '#e65100', // Warning orange — highest authority
  local: '#e65100', // Warning orange — detailed planning
  partial: '#d4a017', // Yellow — overlay/special restrictions
  general: '#0294df', // Popety blue — base zoning (default)
}

/** Text tone per type — darkens the pale yellow so it stays legible on white. */
const ZONING_TYPE_TEXT_COLORS: Record<ZoningType, string> = {
  cantonal: '#e65100',
  local: '#e65100',
  partial: '#a67c00',
  general: '#0294df',
}

/** The non-communal plan types, shown as warnings under the communal zones. */
type SpecialZoningType = Exclude<ZoningType, 'general'>
const SPECIAL_TYPE_ORDER: SpecialZoningType[] = ['cantonal', 'local', 'partial']

/** i18n key suffixes for the "Plan xxx" kicker of each special plan type. */
const PLAN_LABEL_KEY: Record<SpecialZoningType, string> = {
  cantonal: 'planLabelCantonal',
  local: 'planLabelLocal',
  partial: 'planLabelPartial',
}

/** i18n key suffixes for the one-line hint explaining why each plan matters. */
const HINT_KEY: Record<SpecialZoningType, string> = {
  cantonal: 'hintCantonal',
  local: 'hintLocal',
  partial: 'hintPartial',
}

interface ZonesSectionProps {
  generalPlans?: LandZoningReference[]
  cantonalPlans?: LandZoningReference[]
  localPlans?: LandZoningReference[]
  partialPlans?: LandZoningReference[]
}

const ZoneList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ZoneItem = styled.div<{ $color: string }>`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.primary};
    border-left-color: ${({ $color }) => $color};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
`

const ZoneHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

const ZoneInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

const ZoneName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ZoneRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const ZoneCoverage = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--success);
`

const CategoryBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  background: ${({ $color }) => `${$color}1a`};
  color: ${({ $color }) => $color};
`

/* ---- "Other applicable plans" group (cantonal / local / partial) ---- */

const OtherPlansGroup = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed ${({ theme }) => theme.colors.border.light};
`

const OtherPlansLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

const PlanRow = styled.div<{ $color: string }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: 4px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  & + & {
    margin-top: 8px;
  }
`

const PlanRowIcon = styled.span`
  flex-shrink: 0;
  margin-top: 1px;
  display: inline-flex;
`

const PlanRowBody = styled.div`
  flex: 1;
  min-width: 0;
`

const PlanRowKicker = styled.div<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
`

const PlanRowName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 2px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PlanRowHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const PlanRowCoverage = styled.span<{ $color: string }>`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $color }) => $color};
`

const getZoneName = (plan: LandZoningReference, fallback: string): string =>
  plan.lupa_name || plan.lupc_name || plan.lupo_name || plan.lup_name || fallback

const isMeaningfulPlanType = (planType?: string): boolean => {
  if (!planType) return false
  const normalized = planType.toLowerCase()
  return normalized !== 'unknown' && normalized !== 'inconnu'
}

const hasCoverage = (plan: LandZoningReference): boolean =>
  plan.cover_area_perc !== undefined && plan.cover_area_perc > 0

export const ZonesSection: React.FC<ZonesSectionProps> = ({
  generalPlans,
  cantonalPlans,
  localPlans,
  partialPlans,
}) => {
  const { t } = useTranslation('explore')
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const communalPlans = generalPlans || []
  const specialPlansByType: Record<SpecialZoningType, LandZoningReference[]> = {
    cantonal: cantonalPlans || [],
    local: localPlans || [],
    partial: partialPlans || [],
  }
  const specialPlans = SPECIAL_TYPE_ORDER.flatMap((type) =>
    specialPlansByType[type].map((plan) => ({ plan, type }))
  )

  if (communalPlans.length === 0 && specialPlans.length === 0) {
    return null
  }

  const fallbackName = t('propertyTooltip.zones.zone', 'Zone')
  const communalColor = ZONING_TYPE_COLORS.general
  const communalCategoryLabel = t('propertyTooltip.zones.categoryGeneral')

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.zones.title')}
        </SectionTitle>
        <SectionCount>{communalPlans.length}</SectionCount>
      </SectionHeader>
      <SectionContent>
        {communalPlans.length > 0 && (
          <ZoneList>
            {communalPlans.map((plan, index) => {
              const itemId = `general-${plan.id || plan.lup_id}-${index}`
              const isExpanded = expandedItems.has(itemId)
              const hasMeaningfulPlanType = isMeaningfulPlanType(plan.lup_plan_type)

              return (
                <ZoneItem key={itemId} $color={communalColor}>
                  <ZoneHeader onClick={() => toggleExpand(itemId)}>
                    <ZoneInfo>
                      <ZoneName>{getZoneName(plan, fallbackName)}</ZoneName>
                      {hasMeaningfulPlanType && (
                        <div>
                          <ZoneType>{plan.lup_plan_type}</ZoneType>
                        </div>
                      )}
                    </ZoneInfo>
                    <ZoneRight>
                      {hasCoverage(plan) && (
                        <ZoneCoverage>{(plan.cover_area_perc! * 100).toFixed(0)}%</ZoneCoverage>
                      )}
                      <ExpandArrow $expanded={isExpanded} direction="down" />
                    </ZoneRight>
                  </ZoneHeader>
                  <ExpandableContent $expanded={isExpanded}>
                    <ExpandableDetails>
                      <DataRow>
                        <DataLabel>{t('propertyTooltip.zones.category')}</DataLabel>
                        <DataValue>
                          <CategoryBadge $color={communalColor}>
                            {communalCategoryLabel}
                          </CategoryBadge>
                        </DataValue>
                      </DataRow>
                      {hasMeaningfulPlanType && (
                        <DataRow>
                          <DataLabel>{t('propertyTooltip.zones.planType')}</DataLabel>
                          <DataValue>{plan.lup_plan_type}</DataValue>
                        </DataRow>
                      )}
                      {plan.lup_name && (
                        <DataRow>
                          <DataLabel>{t('propertyTooltip.zones.name')}</DataLabel>
                          <DataValue>{plan.lup_name}</DataValue>
                        </DataRow>
                      )}
                      {hasCoverage(plan) && (
                        <DataRow>
                          <DataLabel>{t('propertyTooltip.zones.part')}</DataLabel>
                          <DataValue style={{ color: 'var(--success)' }}>
                            {(plan.cover_area_perc! * 100).toFixed(0)}%
                          </DataValue>
                        </DataRow>
                      )}
                    </ExpandableDetails>
                  </ExpandableContent>
                </ZoneItem>
              )
            })}
          </ZoneList>
        )}

        {specialPlans.length > 0 && (
          <OtherPlansGroup>
            <OtherPlansLabel>
              <Icon name="alert-triangle" size="small" />
              {t('propertyTooltip.zones.otherPlans')}
            </OtherPlansLabel>
            {specialPlans.map(({ plan, type }, index) => {
              const itemId = `${type}-${plan.id || plan.lup_id}-${index}`
              const color = ZONING_TYPE_COLORS[type]
              const textColor = ZONING_TYPE_TEXT_COLORS[type]

              return (
                <PlanRow key={itemId} $color={color}>
                  <PlanRowIcon>
                    <Icon name="alert-triangle" size="small" color={color} />
                  </PlanRowIcon>
                  <PlanRowBody>
                    <PlanRowKicker $color={textColor}>
                      {t(`propertyTooltip.zones.${PLAN_LABEL_KEY[type]}`)} ·{' '}
                      {t('propertyTooltip.zones.inForce')}
                    </PlanRowKicker>
                    <PlanRowName>{getZoneName(plan, fallbackName)}</PlanRowName>
                    <PlanRowHint>{t(`propertyTooltip.zones.${HINT_KEY[type]}`)}</PlanRowHint>
                  </PlanRowBody>
                  {hasCoverage(plan) && (
                    <PlanRowCoverage $color={textColor}>
                      {(plan.cover_area_perc! * 100).toFixed(0)}%
                    </PlanRowCoverage>
                  )}
                </PlanRow>
              )
            })}
          </OtherPlansGroup>
        )}
      </SectionContent>
    </InfoSection>
  )
}
